import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const ARTISTS = [
    {
        name: "Dhee",
        tag: "Vocalist · Chennai / Sydney",
        signature: "Enjoy Enjaami",
        accent: "#F54815",
        image: "https://customer-assets.emergentagent.com/job_music-tech-hub-1/artifacts/er83hd75_Gemini_Generated_Image_2yoahx2yoahx2yoa.jpg",
        bio: "The voice behind the 508M-view phenomenon. Returning with new soundscapes.",
    },
    {
        name: "Diljit Dosanjh",
        tag: "Singer · Punjab → Global",
        signature: "Genre-bending Punjabi pop",
        accent: "#791EE2",
        image: "https://customer-assets.emergentagent.com/job_music-tech-hub-1/artifacts/hlxucim1_Frame%20505.pdf.png",
        bio: "The Coachella headliner rewriting what global Punjabi music sounds like.",
    },
    {
        name: "Roy Woods",
        tag: "R&B · Toronto",
        signature: "OVO Sound alumnus",
        accent: "#F54815",
        image: "https://customer-assets.emergentagent.com/job_music-tech-hub-1/artifacts/gw8xgg43_roy.PNG",
        bio: "Atmospheric R&B with a cinematic edge. A new chapter, scored in Chennai.",
    },
    {
        name: "Shan Vincent de Paul",
        tag: "Hip-Hop · Toronto / Jaffna",
        signature: "Made in Jaffna",
        accent: "#791EE2",
        image: "https://customer-assets.emergentagent.com/job_music-tech-hub-1/artifacts/7w0rp0rx_shawn.PNG",
        bio: "Tamil diaspora rap at its sharpest. Crafting the follow-up to Made in Jaffna.",
    },
];

export const UpcomingArtists = () => {
    return (
        <section
            id="upcoming"
            className="relative py-28 lg:py-36 overflow-hidden"
            data-testid="upcoming-artists-section"
        >
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#791EE2] opacity-10 blur-[160px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#F54815] opacity-10 blur-[160px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 relative">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 lg:mb-20"
                >
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5 mb-6">
                            <Sparkles size={14} className="text-[#F54815]" />
                            <span className="text-xs uppercase tracking-[0.25em] text-white/80 font-medium">
                                Coming soon
                            </span>
                        </div>
                        <h2
                            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]"
                            data-testid="upcoming-heading"
                        >
                            The next chapter
                            <br />
                            of <span className="gradient-text">Maajja</span>.
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-md font-body text-lg">
                        Four artists. Four cultures. One unmistakable sound —
                        dropping soon on Maajja.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                    {ARTISTS.map((artist, i) => (
                        <motion.div
                            key={artist.name}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.7, delay: i * 0.12 }}
                            className="group relative aspect-[3/4] rounded-3xl overflow-hidden border border-white/10 hover:border-white/30 transition-all duration-500 hover:-translate-y-2 bg-[#0a0436]"
                            data-testid={`upcoming-artist-${i}`}
                        >
                            {/* Artist photo */}
                            <img
                                src={artist.image}
                                alt={artist.name}
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                            />

                            {/* Dark gradient overlay for text contrast */}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#030023] via-[#030023]/55 to-[#030023]/10 pointer-events-none" />
                            <div
                                className="absolute inset-0 mix-blend-soft-light opacity-60 pointer-events-none"
                                style={{
                                    background: `linear-gradient(135deg, ${artist.accent}40 0%, transparent 60%)`,
                                }}
                            />

                            {/* Coming soon ribbon */}
                            <div className="absolute top-5 right-5 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/15 z-10">
                                <span
                                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                                    style={{ backgroundColor: artist.accent }}
                                />
                                <span className="text-[10px] uppercase tracking-[0.2em] text-white/90 font-semibold">
                                    Upcoming
                                </span>
                            </div>

                            {/* Animated equalizer */}
                            <div className="absolute top-5 left-5 flex items-end gap-[3px] h-9 opacity-80 group-hover:opacity-100 transition-opacity z-10">
                                {[14, 24, 18, 30, 20, 26].map((h, k) => (
                                    <span
                                        key={k}
                                        className="w-[3px] rounded-sm eq-bar"
                                        style={{
                                            height: `${h}px`,
                                            backgroundColor: artist.accent,
                                            animationDelay: `${k * 0.13}s`,
                                        }}
                                    />
                                ))}
                            </div>

                            {/* Content */}
                            <div className="absolute bottom-0 inset-x-0 p-7 z-10">
                                <p
                                    className="text-[11px] uppercase tracking-[0.2em] mb-3 font-semibold"
                                    style={{ color: artist.accent }}
                                >
                                    {artist.tag}
                                </p>
                                <h3 className="font-heading text-3xl lg:text-[2rem] font-extrabold text-white tracking-tight leading-[1.05] mb-3 drop-shadow-lg">
                                    {artist.name}
                                </h3>
                                <p className="text-sm text-white/75 leading-relaxed font-body mb-4 line-clamp-2">
                                    {artist.bio}
                                </p>
                                <div className="flex items-center gap-2 pt-3 border-t border-white/15">
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-medium">
                                        Signature
                                    </span>
                                    <span className="text-xs text-white font-semibold ml-auto">
                                        {artist.signature}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
